import { Button, Dropdown, Tooltip } from '@getro/rombo';
import { ArrowLeftRight, FileSpreadsheet, Zap, User, UserPlus } from 'lucide-react';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { Box, Card, Flex } from 'rebass/styled-components';
import { activeNetworkSelector, userSelector } from '../../../redux/selectors';
import { useGoogleSync } from '../../../hooks/useGoogleSync';
import { useTierGating } from '../../../hooks/useTierGating';
import UpgradeModal from '../../../components/organisms/upgradeModal';

export const ImportButton = () => {
  const activeNetwork = useSelector(activeNetworkSelector);
  const { linkedinSync } = useSelector(userSelector);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const { isConnected: isGoogleConnected } = useGoogleSync();
  const { hasCapability, isContactsLimitReached } = useTierGating();
  const canImport = hasCapability('contacts_import');
  const upgradeFeatureName = !canImport ? 'contacts_import' : 'contacts_limit';

  const hasGoogleSyncFeature = activeNetwork.features?.includes('google_sync');

  const isLinkedinConnected = Boolean(linkedinSync);
  const isAnyConnected = isLinkedinConnected || (hasGoogleSyncFeature && isGoogleConnected);

  const syncTooltipContent = hasGoogleSyncFeature
    ? 'Connect your LinkedIn or Google account to sync your contacts.'
    : 'Connect your LinkedIn account to sync your contacts.';

  return (
    <Flex sx={{ gap: '8px' }}>
      {canImport && !isAnyConnected && (
        <Tooltip
          position="bottom"
          content={syncTooltipContent}
          trigger={
            <Button
              as={NavLink}
              size="small"
              variant="tertiary"
              icon={Zap}
              to={`/networks/${activeNetwork.slug}/settings/integrations`}
            >
              Sync my contacts
            </Button>
          }
        />
      )}
      {canImport && isAnyConnected && !activeNetwork.shareContacts && (
        <Tooltip
          content="Share your contacts to make them visible to other admins of this network. Manage sharing in 'My networks'."
          trigger={
            <Button
              as={NavLink}
              size="small"
              variant="tertiary"
              icon={ArrowLeftRight}
              to={`/networks/${activeNetwork.slug}/settings/my-networks`}
              iconGap="8px"
            >
              Share my contacts
            </Button>
          }
        />
      )}
      {isContactsLimitReached ? (
        <>
          <Button size="small" variant="accent" icon={UserPlus} onClick={() => setShowUpgradeModal(true)}>
            Add contacts
          </Button>
          <UpgradeModal
            isOpen={showUpgradeModal}
            onClose={() => setShowUpgradeModal(false)}
            featureName={upgradeFeatureName}
          />
        </>
      ) : (
        <>
          <Dropdown
            placement="bottom-end"
            triggerProps={{
              variant: 'accent',
              id: 'add-contacts-dropdown-trigger',
              children: (
                <Flex alignItems="center" fontSize="14px">
                  <Box as={UserPlus} mr="2" width="16px" height="16px" aria-hidden="true" />
                  Add contacts
                </Flex>
              ),
              sx: {
                height: '32px',
              },
            }}
          >
            <Card px="0" py="8px" width="208px">
              <Flex
                as={NavLink}
                to={`/networks/${activeNetwork.slug}/contact/add`}
                height="32px"
                px="16px"
                sx={{
                  fontSize: '14px',
                  gap: '8px',
                  alignItems: 'center',
                  textDecoration: 'none',
                  ':hover': { bg: 'neutral.50' },
                }}
              >
                <Box as={User} width="16px" height="16px" aria-hidden="true" />
                Add single contact
              </Flex>
              <Flex
                {...(canImport
                  ? { as: NavLink, to: `/networks/${activeNetwork.slug}/import-contacts/csv` }
                  : { onClick: () => setShowUpgradeModal(true) })}
                height="32px"
                px="16px"
                sx={{
                  fontSize: '14px',
                  gap: '8px',
                  alignItems: 'center',
                  textDecoration: 'none',
                  cursor: 'pointer',
                  ':hover': { bg: 'neutral.50' },
                }}
              >
                <Box as={FileSpreadsheet} width="16px" height="16px" aria-hidden="true" />
                Import CSV
              </Flex>
            </Card>
          </Dropdown>
          {!canImport && (
            <UpgradeModal
              isOpen={showUpgradeModal}
              onClose={() => setShowUpgradeModal(false)}
              featureName="contacts_import"
            />
          )}
        </>
      )}
    </Flex>
  );
};
